import { Spinner } from 'spin.js';
import { loadDelayedScripts } from "../../sources/index-module";

const doSpinnerByLoadingId = (id, eventType, loadingDivId) => {
  /*    //If the viewport wide version of the spinner exists, show it.
  document.getElementById('viewport-wide-spinner').style.display = 'block';*/

  if (eventType === "blur" || eventType === "change") {
      document.getElementById(id)?.setAttribute('disabled', 'disabled');
  }
  
  //If want to change the text below the spinning div.
  //if (progressText != undefined) { document.getElementById("loadingspinner").innerHTML = progressText; }
  const loadingDiv = document.getElementById(loadingDivId);
  if (loadingDiv) {
      loadingDiv.style.display = 'block';
      // Fade in effect
      let opacity = 0;
      const fadeIn = () => {
          opacity += 0.1;
          if (opacity <= 1) {
              loadingDiv.style.opacity = opacity;
              requestAnimationFrame(fadeIn);
          }
      };
      requestAnimationFrame(fadeIn);
  }
  
  const opts = {
      lines: 12, // The number of lines to draw
      length: 7, // The length of each line
      width: 4, // The line thickness
      radius: 10, // The radius of the inner circle
      color: '#000', // #rgb or #rrggbb
      speed: 1, // Rounds per second
      trail: 60, // Afterglow percentage
      shadow: false, // Whether to render a shadow
      hwaccel: false // Whether to use hardware acceleration
  };
  const target = document.getElementById('' + loadingDivId + '');
  const spinner = new Spinner(opts).spin(target);

  return spinner;
};

const debounce = (callback, delay) => {
    let timeout;
    return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => callback(...args), delay);
    };
};

const findClosest = (element, selector) => {
  while (element) {
    if (element.matches && element.matches(selector)) {
      return element;
    }
    element = element.parentElement;
  }
  return null;
};

const fallbackCopyToClipboard = (text) => {
    const tempInput = document.createElement("input");
    document.body.appendChild(tempInput);
    tempInput.value = text;
    tempInput.select();
    document.execCommand("copy");
    tempInput.remove();
}

const openModal = (modalElement, onOpenCallback = null) => {
    if (!modalElement) return;
    
    modalElement.style.display = 'block';
    
    // Add backdrop overlay
    const overlay = document.createElement('div');
    overlay.className = 'modal-backdrop';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    overlay.style.zIndex = '1040';
    document.body.appendChild(overlay);
    
    document.body.style.overflow = 'hidden';
    
    setTimeout(() => {
        modalElement.classList.add('show');
        
        const event = new CustomEvent('shown.bs.modal');
        modalElement.dispatchEvent(event);
        
        if (typeof onOpenCallback === 'function') {
            onOpenCallback(modalElement);
        }
    }, 10);
}

const closeModal = (modalElement, onCloseCallback = null) => {
    if (!modalElement) return;
    
    modalElement.classList.remove('show');
    
    setTimeout(() => {
        modalElement.style.display = 'none';
        
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
            document.body.removeChild(backdrop);
        }
        
        document.body.style.overflow = '';
        
        const event = new CustomEvent('hidden.bs.modal');
        modalElement.dispatchEvent(event);
        
        if (typeof onCloseCallback === 'function') {
            onCloseCallback(modalElement);
        }
    }, 150);
}

const ModalManager = {
    modals: {},
    
    register(modalId, onOpenCallback = null, onCloseCallback = null) {
        const modalElement = document.getElementById(modalId);
        if (!modalElement) return null;
        
        const modalTriggers = document.querySelectorAll(`[data-target="#${modalId}"], [data-toggle="modal"][href="#${modalId}"]`);
        const closeButtons = modalElement.querySelectorAll('[data-dismiss="modal"]');
        
        const openWithCallback = () => openModal(modalElement, onOpenCallback);
        const closeWithCallback = () => closeModal(modalElement, onCloseCallback);
        
        modalTriggers.forEach(trigger => {
            trigger.addEventListener('click', function(e) {
                e.preventDefault();
                openWithCallback();
            });
        });
        
        closeButtons.forEach(button => {
            button.addEventListener('click', closeWithCallback);
        });
        
        const modalController = {
            open: openWithCallback,
            close: closeWithCallback,
            element: modalElement
        };
        
        this.modals[modalId] = modalController;
        return modalController;
    },
    
    getModal(modalId) {
        return this.modals[modalId] || null;
    },
    
    openModal(modalId) {
        const modal = this.getModal(modalId);
        if (modal) modal.open();
    },
    
    closeModal(modalId) {
        const modal = this.getModal(modalId);
        if (modal) modal.close();
    },
    
    closeAll() {
        Object.values(this.modals).forEach(modal => modal.close());
    },
    
    setupGlobalEvents() {
        // Close on backdrop click
        document.addEventListener('click', e => {
            if (e.target.classList.contains('modal-backdrop')) {
                this.closeAll();
            }
        });
        
        // Close on ESC key
        document.addEventListener('keydown', e => {
            if (e.key === 'Escape') {
                this.closeAll();
            }
        });
    }
};

const initHighChartsBlocks = async (container) => {
    if (container.dataset.highchartsInitialized) return;

    const matchedExps = [
      ...container.innerHTML.matchAll(/<script[\s\S]*?>([\s\S]*?)<\/script>/gi)
    ];
    if (!matchedExps.length) return;

    if (!window.HighchartsScriptIsLoaded) {
      await loadDelayedScripts();
      window.HighchartsScriptIsLoaded = true;
    }

    const prevList = Array.isArray(window.HighchartDataList) ? window.HighchartDataList.slice() : [];
    const prevIds = new Set(prevList.map(o => o && o.renderingContainer).filter(Boolean));

    for (const match of matchedExps) {
      const scriptCode = match[1];
      if (scriptCode && scriptCode.trim() !== '') {
        const newScript = document.createElement('script');
        newScript.appendChild(document.createTextNode(scriptCode));
        document.body.appendChild(newScript);
      }
    }

    const afterList = Array.isArray(window.HighchartDataList) ? window.HighchartDataList : [];
    const delta = afterList.filter(o => o && !prevIds.has(o.renderingContainer));

    const unionById = (a, b) => {
      const map = new Map();
      [...a, ...b].forEach(it => {
        if (!it || !it.renderingContainer) return;
        if (!map.has(it.renderingContainer)) map.set(it.renderingContainer, it);
      });
      return [...map.values()];
    };
    const union = unionById(prevList, delta);

    window.HighchartDataList = delta;

    const hchEvent = new CustomEvent('chartOptionsScriptLoaded');
    document.dispatchEvent(hchEvent);

    window.HighchartDataList = union;

    container.dataset.highchartsInitialized = 'true';

    setTimeout(() => {
      delta.forEach(o => { if (o) o.rendered = true; });
    }, 400);
};

export {
    initHighChartsBlocks,
    doSpinnerByLoadingId,
    debounce,
    findClosest,
    fallbackCopyToClipboard,
    openModal,
    closeModal,
    ModalManager
}